import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { currentlyReading, type LibraryBook } from '@/lib/insights'
import { bookListUrl } from '@/lib/utils'
import { BookThumb, InsightCard, InsightEmpty } from './InsightCard'

const MAX_ITEMS = 3

/** Books in progress with how long they've been open */
export function ContinueReadingCard({ books }: { books: LibraryBook[] }) {
  const { t } = useTranslation()
  const reading = useMemo(() => currentlyReading(books), [books])

  return (
    <InsightCard
      icon="📖"
      title={t('insights.continueReading.title')}
      action={
        reading.length > MAX_ITEMS
          ? { label: t('insights.seeAll'), href: bookListUrl({ status: 'reading' }) }
          : undefined
      }
    >
      {reading.length === 0 ? (
        <InsightEmpty>{t('insights.continueReading.empty')}</InsightEmpty>
      ) : (
        <ul className="space-y-3">
          {reading.slice(0, MAX_ITEMS).map(({ book, daysReading }) => (
            <li key={book.id}>
              <Link
                to={bookListUrl({ type: book.type, status: 'reading' })}
                className="flex items-center gap-3 rounded-md p-1 -m-1 hover:bg-accent"
              >
                <BookThumb src={book.cover_url} title={book.title} />
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">{book.title}</p>
                  <p className="truncate text-xs text-muted-foreground">{book.author}</p>
                  <p className="text-xs text-muted-foreground">
                    {daysReading === null
                      ? t('insights.continueReading.noStartDate')
                      : daysReading === 0
                        ? t('insights.continueReading.startedToday')
                        : t('insights.continueReading.daysReading', { count: daysReading })}
                  </p>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </InsightCard>
  )
}
